import React, { Fragment, useEffect } from "react";
import Styles from "./style.module.scss";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useLocation, useParams } from "react-router-dom";
import { fetchArticleDetail, takeEraseArticleDetails } from "../../../../redux/Reducers/blogSlice";
import { BackLink } from "../../../../components/BackLink";
import { motion } from "framer-motion";
import { CreatedAtArticle } from "../CreatedAtArticle/createdAtArticle";
import JsonLd from "../../../../helpers/JsonLD";
import { MetaData } from "../../../../helpers/MetaData";
import PreLoader from "../../../../components/PreLoader/PreLoader";
import { ShareTo } from "../../../../components/ShareTo";
import NotFound from "../../../NotFound/NotFound";
import { markPrerenderError, markPrerenderReady, resetPrerenderStatus } from "../../../../utils/prerenderStatus";
import { charlottePhotographyServiceAreas, normalizeCanonicalPath } from "../../../../utils/seo";
import { RequestError } from "../../../../components/RequestError";
import { socialPreviews } from "../../../../utils/socialPreviews";

export const BlogArticleDetails: React.FC = () => {
  const { pathname } = useLocation();
  const { url } = useParams<{ url: string }>();
  const dispatch = useAppDispatch();
  const { articleDetails, error, loading } = useAppSelector((state) => state.blog);
  const isNewYorkArchiveStory = url === "fall-session-in-fort-tryon-park-nyc";
  useEffect(() => {
    let active = true;
    const prerenderSource = `blog-article-${url || "unknown"}`;
    resetPrerenderStatus(prerenderSource);
    dispatch(fetchArticleDetail(url))
      .unwrap()
      .then((article) => {
        if (!active) return;
        article?.title && article?.details?.description
          ? markPrerenderReady(prerenderSource)
          : markPrerenderError(`${prerenderSource}-empty`);
      })
      .catch(() => active && markPrerenderError(`${prerenderSource}-request`));

    return () => {
      active = false;
    };
  }, [url, dispatch]);

  const formattedDate = new Date(articleDetails.createdAt).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const canonicalUrl = normalizeCanonicalPath(`https://alenalobacheva.com${pathname}`);
  const pageDescription = `A photography story about a ${articleDetails?.typePhotoSession} session, published on ${formattedDate}.`;
  const socialPreview = isNewYorkArchiveStory ? socialPreviews.blogFortTryon : { imageOG: articleDetails?.imagePrev };
  const socialImage = socialPreview.imageOG;

  const blogArticleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: articleDetails?.title,
        description: pageDescription,
        image: socialImage,
        isPartOf: { "@id": "https://alenalobacheva.com/#website" },
      },
      {
        "@type": "BlogPosting",
        "@id": `${canonicalUrl}#blog-posting`,
        headline: articleDetails?.title,
        description: pageDescription,
        image: socialImage ? [socialImage] : undefined,
        datePublished: articleDetails?.createdAt,
        dateModified: articleDetails?.createdAt,
        inLanguage: "en-US",
        author: {
          "@type": "Person",
          name: "Alena Lobacheva",
          url: "https://alenalobacheva.com/about/",
        },
        publisher: { "@id": "https://alenalobacheva.com/#localbusiness" },
        mainEntityOfPage: { "@id": `${canonicalUrl}#webpage` },
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://alenalobacheva.com/#localbusiness",
        name: "Alena Lobacheva Photography",
        url: "https://alenalobacheva.com/",
        telephone: "+1-516-468-4837",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Charlotte",
          addressRegion: "NC",
          addressCountry: "US",
        },
        areaServed: charlottePhotographyServiceAreas,
      },
    ],
  };

  const onClickButtonBack = () => {
    dispatch(
      takeEraseArticleDetails({
        title: "",
        url: "",
        imagePrev: "",
        typePhotoSession: "",
        createdAt: "",
        details: {
          images: [],
          description: "",
        },
      })
    );
  };

  if (loading.getArticleDetail) {
    return <PreLoader />;
  }

  if (error.getArticleDetail === "not-found") {
    return <NotFound />;
  }

  if (error.getArticleDetail) {
    return (
      <RequestError
        message="We couldn’t load this story. Check your connection and try again."
        onRetry={() => dispatch(fetchArticleDetail(url))}
      />
    );
  }

  return (
    <Fragment>
      <JsonLd data={blogArticleSchema} />
      <MetaData
        title={`${articleDetails?.title}`}
        description={pageDescription}
        canonicalLink={canonicalUrl}
        imageAltOG="blog photography"
        {...socialPreview}
        type="article"
        titleOG={`${articleDetails?.title}`}
        descriptionOG={pageDescription}
      />
      <div className={Styles.blogArticleDetails}>
        <div className={Styles.blogArticleDetails__linkBackContainer}>
          <BackLink linkName="back" path="/blog/" onClick={onClickButtonBack} />
          <ShareTo articleDetails={articleDetails} />
        </div>
        <h1 className={Styles.blogArticleDetails__title}>{articleDetails?.title}</h1>
        {isNewYorkArchiveStory && (
          <p className={Styles.blogArticleDetails__archiveNotice}>
            From the archive: this session was photographed while I was working in New York. I am now based in
            Charlotte, North Carolina.
          </p>
        )}
        <motion.div className={Styles.blogArticleDetails__titleUnderLine} />
        <h2 className={Styles.blogArticleDetails__typePhotoSession}>{`${articleDetails?.typePhotoSession}`}</h2>
        <div className={Styles.blogArticleDetails__typeContainer}>
          <CreatedAtArticle createdAt={articleDetails?.createdAt} />
        </div>
        <div
          className={Styles.blogArticleDetails__descriptionContainer}
          dangerouslySetInnerHTML={{ __html: articleDetails?.details.description }}
        />
        <ul className={Styles.blogArticleDetails__imagesContainer}>
          {articleDetails?.details.images.map((image: string, index: number) => {
            return (
              <img
                className={Styles.blogArticleDetails__image}
                src={image}
                alt={`${articleDetails?.title} — view ${index + 1}`}
                key={index}
              />
            );
          })}
        </ul>
      </div>
    </Fragment>
  );
};
